import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '../../lib/api-fetcher.js';
import type {
  CreateContactAttemptInput,
  CreateRecoveryEntryInput
} from './schemas';

export interface OverdueMemberItem {
  installmentId: string;
  memberId: string;
  groupId: string;
  groupCode: string;
  customerName: string;
  ticketNumber: number;
  isPrized: boolean;
  agentId: string;
  agentName: string;
  dueAmount: number;
  daysOverdue: number;
  dueDate: string;
  lastContactDate?: string;
  nextFollowupDate?: string;
}

export interface DefaultRecordListItem {
  id: string;
  memberId: string;
  groupId: string;
  customerName: string;
  defaultDate: string;
  outstandingPrincipal: number;
  outstandingPenalty: number;
  status: 'active' | 'partial' | 'resolved' | 'written_off';
}

export interface RecoveryEntry {
  id: string;
  defaultRecordId: string;
  amountPaise: number;
  paidBy: string | null;
  notes: string | null;
  recoveredAt: string;
}

export function useOverdueMembers(filters?: { bucket?: string; orgUnitId?: string }) {
  const queryParams = new URLSearchParams();
  if (filters?.bucket) queryParams.append('bucket', filters.bucket);
  if (filters?.orgUnitId) queryParams.append('orgUnitId', filters.orgUnitId);
  const queryString = queryParams.toString();
  const path = `/staff/v1/collections/overdue/installments${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: ['overdue', 'installments', filters],
    queryFn: () => fetcher.get<{ data: OverdueMemberItem[]; meta: Record<string, unknown> }>(path),
  });
}

export function useCreateContactAttempt(installmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateContactAttemptInput) =>
      fetcher.post('/staff/v1/collections/overdue/contact-attempts', { installmentId, ...data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overdue'] });
    }
  });
}

export function useDefaultRecords(filters?: { status?: string; orgUnitId?: string }) {
  const queryParams = new URLSearchParams();
  if (filters?.status) queryParams.append('status', filters.status);
  if (filters?.orgUnitId) queryParams.append('orgUnitId', filters.orgUnitId);
  const queryString = queryParams.toString();
  const path = `/staff/v1/collections/overdue/defaults${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: ['default-records', 'list', filters],
    queryFn: () => fetcher.get<{ data: DefaultRecordListItem[]; meta: Record<string, unknown> }>(path),
  });
}

export function useRecoveryEntries(defaultRecordId: string) {
  return useQuery({
    queryKey: ['recovery-entries', defaultRecordId],
    queryFn: () => fetcher.get<RecoveryEntry[]>(`/staff/v1/collections/overdue/defaults/${defaultRecordId}/recovery`),
    enabled: !!defaultRecordId,
  });
}

export function useCreateRecoveryEntry(defaultRecordId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRecoveryEntryInput) =>
      fetcher.post(`/staff/v1/collections/overdue/defaults/${defaultRecordId}/recovery`, { defaultRecordId, ...data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recovery-entries'] });
      queryClient.invalidateQueries({ queryKey: ['default-records'] });
    }
  });
}

export function useCreateWaiverRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { installmentId: string; reason: string; requestedAmountPaise: number }) =>
      fetcher.post('/staff/v1/collections/overdue/waiver-requests', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overdue'] });
    }
  });
}

/** Maker step: logs the write-off request, pending a second user's approval (see
 * useApproveWriteOff below) — does not immediately write off the balance. */
export function useInitiateWriteOff(defaultRecordId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      fetcher.post(`/staff/v1/defaults/${defaultRecordId}/write-off`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['default-records'] });
      queryClient.invalidateQueries({ queryKey: ['default-record', 'detail', defaultRecordId] });
    }
  });
}

/** Checker step: a different user than the one who called useInitiateWriteOff must approve
 * before the record actually moves to WRITTEN_OFF (server enforces maker != checker). */
export function useApproveWriteOff(defaultRecordId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetcher.post(`/staff/v1/defaults/${defaultRecordId}/write-off/approve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['default-records'] });
      queryClient.invalidateQueries({ queryKey: ['default-record', 'detail', defaultRecordId] });
    }
  });
}

export interface LateFeePayment {
  id: string;
  receiptNumber: string;
  receiptDate: string;
  amountPaise: number;
  status: string;
  customerId: string | null;
  customerName: string | null;
  customerNumber: string | null;
  agentNumber: string | null;
}

export interface LateFeePaymentsMeta {
  total: number;
  page: number;
  limit: number;
}

export function useLateFeePayments(filters?: { page?: number; limit?: number; date?: string; customerId?: string }) {
  const queryParams = new URLSearchParams();
  if (filters) {
    if (filters.page) queryParams.append('page', String(filters.page));
    if (filters.limit) queryParams.append('limit', String(filters.limit));
    if (filters.date) queryParams.append('date', filters.date);
    if (filters.customerId) queryParams.append('customerId', filters.customerId);
  }
  const queryString = queryParams.toString();
  const path = `/staff/v1/collections/overdue/late-fees/payments${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: ['late-fees', 'payments', filters],
    queryFn: () => fetcher.get<{ data: LateFeePayment[]; meta: LateFeePaymentsMeta }>(path),
  });
}

export function useLateFeePaymentDetails(id: string) {
  return useQuery({
    queryKey: ['late-fees', 'payment', id],
    queryFn: () => fetcher.get<{ data: Record<string, unknown> }>(`/staff/v1/collections/overdue/late-fees/payments/${id}`),
    enabled: !!id,
  });
}

export function useBounceLateFeePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetcher.post(`/staff/v1/collections/overdue/late-fees/payments/${id}/bounce`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['late-fees'] });
    },
  });
}

export function useCreateLateFeeDebit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { installmentId: string; amountPaise: number; accrualDate?: string }) =>
      fetcher.post<{ data: Record<string, unknown> }>(`/staff/v1/collections/overdue/late-fees/debit`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-late-fees'] });
    },
  });
}
